import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['textarea']
  
  connect() {
    console.log('Markdown editor controller connected!')
    
    // Ensure we have a textarea target
    if (!this.hasTextareaTarget) {
      console.error('No textarea target found for markdown editor')
      return
    }
    
    // Load EasyMDE dynamically to avoid race conditions
    this.loadEasyMDE()
  }
  
  async loadEasyMDE() {
    try {
      // Check if EasyMDE is already loaded
      if (typeof EasyMDE === 'undefined') {
        // Try importing dynamically
        const module = await import('easymde')
        window.EasyMDE = module.default || module.EasyMDE
      }
      
      // Wait for next tick to ensure DOM is ready
      requestAnimationFrame(() => {
        this.initializeEditor()
      })
    } catch (error) {
      console.error('Failed to load EasyMDE:', error)
      // Retry after a short delay
      setTimeout(() => this.retryInitialization(), 500)
    }
  }
  
  initializeEditor() {
    // Check if editor already exists (prevents double initialization)
    if (this.editor) {
      console.log('Editor already initialized')
      return
    }
    
    // Check if textarea is still in DOM
    if (!document.body.contains(this.textareaTarget)) {
      console.error('Textarea no longer in DOM')
      return
    }
    
    try {
      console.log('Initializing EasyMDE editor...')
      
      this.editor = new EasyMDE({
        element: this.textareaTarget,
        spellChecker: false,
        status: ['lines', 'words'],
        toolbar: [
          'bold', 'italic', 'heading', '|',
          'quote', 'unordered-list', 'ordered-list', '|',
          'link', 'image', '|',
          'preview', 'side-by-side', 'fullscreen', '|',
          'guide'
        ],
        autosave: {
          enabled: true,
          uniqueId: `article-${this.getArticleId()}`,
          delay: 1000,
          submit_delay: 5000,
          timeFormat: {
            locale: 'en-US',
            format: {
              hour: '2-digit',
              minute: '2-digit',
            },
          },
          text: 'Autosaved: '
        },
        renderingConfig: {
          singleLineBreaks: false,
          codeSyntaxHighlighting: true,
        },
        previewRender: (plainText, preview) => {
          // Use setTimeout to allow default preview to render first
          setTimeout(() => {
            // Apply Tailwind prose classes to preview
            preview.classList.add('prose', 'prose-lg', 'max-w-none')
          }, 1)
          // Return the default rendering
          return this.editor.markdown(plainText)
        },
        placeholder: "Type your content here...",
        promptURLs: true,
        uploadImage: false, // Disable for now, can enable with custom handler
        shortcuts: {
          drawTable: "Cmd-Alt-T",
          toggleFullScreen: "F11"
        }
      })
      
      console.log('EasyMDE editor initialized successfully!')
      
      // Ensure form submission includes the editor content
      this.setupFormSubmission()
      
    } catch (error) {
      console.error('Failed to initialize EasyMDE:', error)
      // Retry once more after a delay
      setTimeout(() => this.retryInitialization(), 1000)
    }
  }
  
  retryInitialization() {
    if (this.retryCount === undefined) {
      this.retryCount = 0
    }
    
    this.retryCount++
    
    if (this.retryCount <= 3) {
      console.log(`Retrying EasyMDE initialization (attempt ${this.retryCount})...`)
      this.loadEasyMDE()
    } else {
      console.error('Failed to initialize EasyMDE after 3 attempts')
      // Fall back to plain textarea
      this.textareaTarget.style.minHeight = '400px'
      this.textareaTarget.classList.add('w-full', 'px-3', 'py-2', 'border', 'border-gray-300', 'rounded-md')
    }
  }
  
  setupFormSubmission() {
    // Find the form
    const form = this.textareaTarget.closest('form')
    if (!form) return
    
    // Update textarea value before form submission
    form.addEventListener('submit', (e) => {
      if (this.editor) {
        // Ensure the textarea has the latest content
        this.textareaTarget.value = this.editor.value()
      }
    })
  }
  
  getArticleId() {
    // Try to extract article ID from the form action or URL
    const form = this.textareaTarget.closest('form')
    if (form && form.action) {
      const match = form.action.match(/articles\/(\d+)/)
      if (match) return match[1]
    }
    
    // Fallback to URL
    const urlMatch = window.location.pathname.match(/articles\/(\d+)/)
    return urlMatch ? urlMatch[1] : 'new'
  }
  
  disconnect() {
    console.log('Markdown editor disconnecting...')
    
    if (this.editor) {
      // Save any pending changes
      if (this.editor.codemirror) {
        this.textareaTarget.value = this.editor.value()
      }
      
      // Clean up the editor
      this.editor.toTextArea()
      this.editor = null
    }
  }
}